<template>
  <LoadOnVisible v-on:intersected="loadChartData">
    <b-card no-body>
      <b-card-header class="pt-0 pb-0">
        <div class="w-100 pb-2">
          <b-row class="align-items-center">
            <div class="col">
              <b-card-title class="mb-1 pt-3" title-tag="h5">
                {{ chartTitle }}
              </b-card-title>
            </div>

            <div class="col-auto d-flex align-items-center">
              <div v-if="!isLoadingChartData && chartDataLoaded" class="mr-3 pt-3">
                <strong class="text-primary">${{ formatCurrency(totalAmount) }}</strong>
              </div>

              <div class="mr-3 pt-3">
                <b-form-group class="mb-0">
                  <b-form-radio-group
                    v-model="chartMode"
                    :options="chartModeOptions"
                    buttons
                    button-variant="outline-primary"
                    size="sm"
                  ></b-form-radio-group>
                </b-form-group>
              </div>

              <div v-if="showSeriesFilter" class="mr-3 pt-3">
                <b-form-group class="mb-0">
                  <b-form-input
                    v-model="seriesFilter"
                    :placeholder="filterPlaceholder"
                    size="sm"
                    @input="filterChartData"
                  ></b-form-input>
                </b-form-group>
              </div>

              <div v-if="showWidgetFilters" class="d-flex align-items-center flex-wrap">
                <div class="mr-2">
                  <b-form-group class="pt-1 mb-0">
                    <b-input-group prepend="From">
                      <b-form-datepicker
                        v-model="fromDate"
                        :max="toDate"
                        @input="reloadChart"
                      ></b-form-datepicker>
                    </b-input-group>
                  </b-form-group>
                </div>

                <div>
                  <b-form-group class="pt-1 mb-0">
                    <b-input-group prepend="To">
                      <b-form-datepicker
                        v-model="toDate"
                        :min="fromDate"
                        @input="reloadChart"
                      ></b-form-datepicker>
                    </b-input-group>
                  </b-form-group>
                </div>
              </div>
            </div>
          </b-row>
        </div>
      </b-card-header>

      <b-card-body>
        <div class="text-center" v-if="isLoadingChartData">
          <b-spinner
            variant="primary"
            class="m-5"
            style="height: 4rem; width: 4rem"
            label="Loading..."
          ></b-spinner>
        </div>
        <div v-else-if="hasError" class="text-center py-5">
          <i class="fa fa-exclamation-triangle fa-3x text-danger mb-3"></i>
          <p class="text-danger mb-3">Failed to load chart data. Please try again.</p>
          <b-button variant="primary" @click="reloadChart">
            <i class="fa fa-refresh"></i> Retry
          </b-button>
        </div>
        <vue-apex-charts
          v-else-if="chartDataLoaded && chartMode === 'line'"
          type="line"
          ref="chart"
          height="400"
          :options="chartData.chartOptions"
          :series="chartData.series"
        />
        <vue-apex-charts
          v-else-if="chartDataLoaded && chartMode === 'piechart'"
          type="pie"
          ref="chart"
          height="400"
          :options="pieChartData.chartOptions"
          :series="pieChartData.series"
        />
        <div v-else class="text-center py-5">
          <p class="text-muted">No data available for the selected period</p>
        </div>
      </b-card-body>
    </b-card>
  </LoadOnVisible>
</template>

<script>
import { myCommonDataMixin } from '@/helpers/utils/vueMixins.js';
import { $themeColors } from '@/themeConfig';
import VueApexCharts from 'vue-apexcharts';
import Ripple from 'vue-ripple-directive';
import { LoadOnVisible } from '@/views/components/custom';

export default {
  mixins: [myCommonDataMixin],
  props: {
    groupBy: {
      type: String,
      required: false,
      default: 'bysurcharge'
    },
    brandId: {
      required: false,
      default: ''
    },
    warehouseId: {
      required: false,
      default: ''
    },
    storefrontId: {
      required: false,
      default: ''
    },
    chargeLabel: {
      type: [String, Array],
      required: false,
      default: ''
    },
    chargeType: {
      required: false,
      default: ''
    },
    showWidgetFilters: {
      type: Boolean,
      required: false,
      default: true
    },
    filterFromDate: {
      type: String,
      required: false,
      default: null
    },
    filterToDate: {
      type: String,
      required: false,
      default: null
    }
  },
  directives: {
    Ripple
  },
  components: {
    VueApexCharts,
    LoadOnVisible
  },
  data() {
    var todaysDate = new Date();
    var lastQuarterFromDate = new Date();
    lastQuarterFromDate.setMonth(todaysDate.getMonth() - 3);

    return {
      isLoadingChartData: true,
      chartDataLoaded: false,
      hasError: false,
      retryCount: 0,
      maxRetries: 2,
      isInitialLoad: true,
      chartMode: 'line',
      chartModeOptions: [
        { text: 'Line', value: 'line' },
        { text: 'Pie', value: 'piechart' }
      ],
      fromDate: this.filterFromDate || lastQuarterFromDate.toISOString().split('T')[0],
      toDate: this.filterToDate || todaysDate.toISOString().split('T')[0],
      totalRecords: 0,
      totalAmount: 0,
      seriesFilter: '',
      originalSeriesData: [],
      chartData: {
        series: [],
        chartOptions: {
          chart: {
            zoom: {
              enabled: true
            },
            toolbar: {
              show: true,
              tools: {
                download: true,
                selection: true,
                zoom: true,
                zoomin: true,
                zoomout: true,
                pan: true,
                reset: true
              },
              export: {
                csv: {
                  filename: 'billing-charge-stats',
                  headerCategory: 'Date',
                  headerValue: 'Amount'
                },
                svg: {
                  filename: 'billing-charge-stats'
                },
                png: {
                  filename: 'billing-charge-stats'
                }
              }
            },
            animations: {
              enabled: false
            }
          },
          markers: {
            size: 3,
            strokeWidth: 2,
            strokeOpacity: 1,
            strokeColors: [$themeColors.light]
          },
          colors: [],
          dataLabels: {
            enabled: false
          },
          stroke: {
            curve: 'smooth',
            width: 2
          },
          grid: {
            xaxis: {
              lines: {
                show: true
              }
            }
          },
          legend: {
            show: true,
            position: 'right'
          },
          tooltip: {
            shared: true,
            intersect: false,
            x: {
              show: true,
              format: 'dd MMM yyyy'
            },
            y: {
              formatter: null,
              title: {
                formatter: function(seriesName) {
                  return seriesName;
                }
              }
            }
          },
          xaxis: {
            type: 'datetime',
            labels: {
              show: true,
              rotate: -45,
              rotateAlways: false,
              hideOverlappingLabels: true,
              showDuplicates: false,
              trim: false,
              datetimeFormatter: {
                year: 'yyyy',
                month: "MMM 'yy",
                day: 'dd MMM',
                hour: 'HH:mm'
              }
            }
          },
          yaxis: {
            title: {
              text: 'Amount (USD)'
            },
            labels: {
              formatter: function(value) {
                return '$' + value.toFixed(2);
              }
            }
          }
        }
      },
      pieChartData: {
        series: [],
        chartOptions: {
          chart: {
            toolbar: {
              show: false
            }
          },
          labels: [],
          colors: [],
          legend: {
            show: true,
            position: 'right'
          },
          dataLabels: {
            enabled: true
          },
          tooltip: {
            y: {
              formatter: function(value) {
                return '$' + value.toFixed(2);
              }
            }
          }
        }
      }
    };
  },
  created() {
  },
  computed: {
    chartTitle() {
      var titles = {
        'bysurcharge': 'Surcharge/Accessorial Charges - by Label',
        'byservice': 'Freight Charges - by Carrier',
        'byservice': 'Freight Charges - by Service',
        'byzoneid': 'Freight Charge Count - by Zone',
        'byavgaccessorial': 'Avg Accessorial - by Carrier & Service',
        'byfuel': 'Fuel Surcharge Charges - by Carrier & Service',
        'byclient': 'Billing Charges - by Client',
        'bychargetype': 'Billing Charges - by Charge Type'
      };
      return titles[this.groupBy] || 'Client Billing Stats';
    },
    showSeriesFilter() {
      return true;
    },
    filterPlaceholder() {
      if (this.groupBy === 'bysurcharge') {
        return 'Filter by label...';
      } else if (this.groupBy === 'byservice') {
        return 'Filter by carrier...';
      } else if (this.groupBy === 'byservice' || this.groupBy === 'byavgaccessorial' || this.groupBy === 'byfuel') {
        return 'Filter by carrier/service...';
      } else if (this.groupBy === 'byzoneid') {
        return 'Filter by zone...';
      } else if (this.groupBy === 'byclient') {
        return 'Filter by client...';
      } else if (this.groupBy === 'bychargetype') {
        return 'Filter by charge type...';
      }
      return 'Filter...';
    }
  },
  watch: {
    brandId(newVal) {
      this.reloadChart();
    },
    warehouseId(newVal) {
      this.reloadChart();
    },
    chargeLabel(newVal) {
      this.reloadChart();
    },
    chargeType(newVal) {
      this.reloadChart();
    },
    filterFromDate(newVal) {
      if (newVal && newVal !== this.fromDate) {
        this.fromDate = newVal;
        this.reloadChart();
      }
    },
    filterToDate(newVal) {
      if (newVal && newVal !== this.toDate) {
        this.toDate = newVal;
        this.reloadChart();
      }
    }
  },
  methods: {
    formatCurrency(value) {
      return value.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    generateColorFromString(str) {
      // Generate a consistent hash from string
      var hash = 0;
      for (var i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
      }

      // Convert hash to HSL color for better distribution
      var h = Math.abs(hash % 360);
      var s = 65 + (Math.abs(hash) % 20); // 65-85% saturation
      var l = 45 + (Math.abs(hash >> 8) % 15); // 45-60% lightness

      return 'hsl(' + h + ', ' + s + '%, ' + l + '%)';
    },
    setDateRange(daysBack) {
      var todaysDate = new Date();
      var pastDate = new Date();
      pastDate.setDate(todaysDate.getDate() - daysBack);

      this.fromDate = pastDate.toISOString().split('T')[0];
      this.toDate = todaysDate.toISOString().split('T')[0];
      this.reloadChart();
    },
    reloadChart() {
      if (!this.isLoadingChartData) {
        this.retryCount = 0;
        this.isInitialLoad = false;
        this.loadChartData();
      }
    },
    filterChartData() {
      var filterText = this.seriesFilter;

      if (!filterText || filterText.trim() === '') {
        // No filter, show all data
        this.updateChartWithData(this.originalSeriesData);
        return;
      }

      // Filter series by name
      var filterTextLower = filterText.toLowerCase();
      var filteredSeries = this.originalSeriesData.filter(function(series) {
        return series.name.toLowerCase().includes(filterTextLower);
      });

      this.updateChartWithData(filteredSeries);
    },
    updateChartWithData(seriesData) {
      if (seriesData && seriesData.length > 0) {
        this.chartDataLoaded = true;

        var self = this;
        var colors = seriesData.map(function(series) {
          return self.generateColorFromString(series.name);
        });

        // Determine if this is the zone chart (shows counts instead of USD)
        var isZoneChart = this.groupBy === 'byzoneid';

        this.chartData.series = seriesData;
        this.chartData.chartOptions = Object.assign({}, this.chartData.chartOptions, {
          colors: colors,
          yaxis: {
            title: {
              text: isZoneChart ? 'Count' : 'Amount (USD)'
            },
            labels: {
              formatter: function(value) {
                if (isZoneChart) {
                  return Math.round(value);
                }
                return '$' + value.toFixed(2);
              }
            }
          },
          tooltip: {
            shared: true,
            intersect: false,
            x: {
              show: true,
              format: 'dd MMM yyyy'
            },
            y: {
              formatter: function(value) {
                if (isZoneChart) {
                  return Math.round(value) + ' entries';
                }
                return '$' + value.toFixed(2);
              },
              title: {
                formatter: function(seriesName) {
                  return seriesName;
                }
              }
            }
          }
        });

        // Prepare pie chart data by summing all datapoints for each series
        var pieLabels = [];
        var pieSeries = [];
        seriesData.forEach(function(series) {
          pieLabels.push(series.name);
          var total = series.data.reduce(function(sum, dp) {
            return sum + dp.y;
          }, 0);
          pieSeries.push(total);
        });

        this.pieChartData.series = pieSeries;
        this.pieChartData.chartOptions = Object.assign({}, this.pieChartData.chartOptions, {
          labels: pieLabels,
          colors: colors,
          tooltip: {
            y: {
              formatter: function(value) {
                if (isZoneChart) {
                  return Math.round(value) + ' entries';
                }
                return '$' + value.toFixed(2);
              }
            }
          }
        });
      } else {
        this.chartDataLoaded = false;
        this.chartData.series = [];
        this.pieChartData.series = [];
      }
    },
    loadChartData() {
      this.isLoadingChartData = true;
      this.chartDataLoaded = false;
      this.hasError = false;
      this.seriesFilter = '';

      var requestData = {
        fromDate: this.fromDate,
        toDate: this.toDate,
        groupBy: this.groupBy
      };

      if (this.brandId) {
        requestData.brandId = this.brandId;
      }

      if (this.warehouseId) {
        requestData.warehouseId = this.warehouseId;
      }

      if (this.storefrontId) {
        requestData.storefrontId = this.storefrontId;
      }

      if (this.chargeLabel) {
        requestData.chargeLabels = Array.isArray(this.chargeLabel) ? this.chargeLabel : [this.chargeLabel];
      }

      if (this.chargeType) {
        requestData.chargeType = this.chargeType;
      }

      this.$http
        .post('/api/analytics/billing-charge-stats', requestData)
        .then(resp => {
          this.isLoadingChartData = false;
          this.retryCount = 0;
          this.isInitialLoad = false;

          if (resp.data) {
            this.totalRecords = resp.data.totalRecords;
            this.totalAmount = resp.data.totalAmount;

            if (resp.data.series && resp.data.series.length > 0) {
              var seriesData = resp.data.series.map(function(series) {
                return {
                  name: series.seriesName,
                  data: series.dataPoints.map(function(dp) {
                    return {
                      x: new Date(dp.date),
                      y: dp.count
                    };
                  })
                };
              });

              // Store original data for filtering
              this.originalSeriesData = seriesData;

              // Update chart with data
              this.updateChartWithData(seriesData);
            } else {
              this.chartDataLoaded = false;
              this.chartData.series = [];
              this.pieChartData.series = [];
              this.originalSeriesData = [];
            }
          } else {
            this.chartDataLoaded = false;
            this.chartData.series = [];
            this.pieChartData.series = [];
            this.originalSeriesData = [];
            this.totalRecords = 0;
            this.totalAmount = 0;
          }
        })
        .catch(err => {
          console.error('Error loading Client Billing Stats:', err);

          // Auto-retry on initial load only
          if (this.isInitialLoad && this.retryCount < this.maxRetries) {
            this.retryCount++;
            var self = this;
            setTimeout(function() {
              self.loadChartData();
            }, 3000);
            return;
          }

          this.isLoadingChartData = false;
          this.chartDataLoaded = false;
          this.hasError = true;
          this.isInitialLoad = false;
          this.chartData.series = [];
          this.pieChartData.series = [];
          this.originalSeriesData = [];
          this.totalRecords = 0;
          this.totalAmount = 0;
        });
    }
  },
  mounted() {
  }
};
</script>
